import React, { Suspense } from 'react';
import {
  Redirect,
  Route,
  Switch,
  useLocation,
} from 'react-router-dom';
import { useAppSelector } from 'redux/hooks';
import {
  selectPermission, selectToken,
} from 'redux/global';
import Layout from 'pages/Layout/Layout';
import Loader from 'pages/components/Loader/Loader';
import { getRouteLoadingMessage } from 'pages/components/Loader/getRouteLoadingMessage';
import React18BrowserRouter from 'routing/React18BrowserRouter';
import { lazyWithChunkReload } from 'routing/lazyWithChunkReload';
import {
  PERM,
  canAccessCompanyDepartmentsPage,
  canAccessDepartmentsPage,
  canAccessMetrics,
  canAccessCommandCenterNav,
  canAccessFormsNav,
  canAccessMasterDataNav,
  canAccessActivityLogsNav,
  canAccessMetricsNav,
} from 'pages/Settings/redux';
import { AnalyticsTracker } from 'utils/analytics';
import { ChatwootBubbleGate } from 'utils/chatwootBubble';
import { ERP_PATHS, ERP_REPORTING_BASE, ERP_REPORTING_SLUG_LEGACY, ERP_OPERATIONS_SLUG, ERP_OPERATIONS_SLUG_LEGACY, erpSettingsRoutePaths } from 'pages/Layout/erpModulePaths';

// lazyWithChunkReload: after a prod deploy, stale main.js can request missing
// hashed chunks — auto-reload once (same fix as a manual refresh).
const Logs = lazyWithChunkReload(() => import('pages/Logs/Logs'));
const Metrics = lazyWithChunkReload(() => import('pages/Metrics/Metrics'));
const CommandCenter = lazyWithChunkReload(() => import('pages/CommandCenter/CommandCenter'));
const Forms = lazyWithChunkReload(() => import('pages/Forms/Forms'));
const Reporting = lazyWithChunkReload(() => import('pages/Reporting/Reporting'));
const Compliance = lazyWithChunkReload(() => import('pages/Compliance/Compliance'));
const Records = lazyWithChunkReload(() => import('pages/Records/Records'));
const Settings = lazyWithChunkReload(() => import('pages/Settings/Settings'));
const NotFound = lazyWithChunkReload(() => import('pages/NotFound/NotFound'));
const Forgot = lazyWithChunkReload(() => import('pages/Forgot/Forgot'));
const SetPassword = lazyWithChunkReload(() => import('pages/SetPassword/SetPassword'));
const RegisterPassword = lazyWithChunkReload(() => import('pages/RegisterPassword/RegisterPassword'));
const SignIn = lazyWithChunkReload(() => import('pages/SignIn/SignIn'));
const SignUp = lazyWithChunkReload(() => import('pages/SignUp/SignUp'));
const Live = lazyWithChunkReload(() => import('pages/Live/Live'));
const AddOrganization = lazyWithChunkReload(() => import('pages/AddOrganization/AddOrganization'));
const UnSubscribe = lazyWithChunkReload(() => import('pages/UnSubscribe/UnSubscribe'));
const EmployeeInvite = lazyWithChunkReload(() => import('pages/EmployeeInvite/EmployeeInvite'));

(document.body.style as any).zoom = '100.06%';

const RouteChunkFallback = function RouteChunkFallback() {
  // After session boot, lazy chunks still suspend on first visit — show the
  // in-app loader instead of an empty (white) page. Session boot already
  // overlays Loader; stacking the same overlay is a no-op visually.
  const { pathname } = useLocation();
  return <Loader instant message={getRouteLoadingMessage(pathname)} />;
};

const toActivityLogs = ({ location }: { location: { search: string } }) => (
  <Redirect to={{ pathname: ERP_PATHS.activityLogs, search: location.search }} />
);

const toDepartments = ({ location }: { location: { search: string } }) => (
  <Redirect to={{ pathname: ERP_PATHS.departments, search: location.search }} />
);

const toWorkflowRecords = ({ location }: { location: { search: string } }) => (
  <Redirect to={{ pathname: ERP_PATHS.recordsKind('workflow'), search: location.search }} />
);

const toOrgDepartmentsRecords = ({ location }: { location: { search: string } }) => (
  <Redirect to={{ pathname: ERP_PATHS.recordsKind('department'), search: location.search }} />
);

const Routing = function Routing() {
  const token = useAppSelector(selectToken);
  const permission = useAppSelector(selectPermission);

  return (
    <React18BrowserRouter>
      <AnalyticsTracker />
      <ChatwootBubbleGate />
      <Layout>
        {/* In-app loader only — never the refresh logo splash */}
        <Suspense fallback={<RouteChunkFallback />}>
          <Switch>
            <Route exact path="/">
              <SignIn />
            </Route>
            <Route exact path="/pricing">
              <SignIn />
            </Route>
            <Route exact path="/unsubscribe">
              <UnSubscribe />
            </Route>
            <Route
              exact
              path="/employee-invite/:token"
              render={({ match }) => (
                <Redirect to={`/form/${match.params.token}`} />
              )}
            />
            <Route path="/form/:token">
              <EmployeeInvite />
            </Route>
            <Route exact path="/forgot">
              <Forgot />
            </Route>
            <Route exact path="/forgot/:key/:id">
              <SetPassword />
            </Route>
            <Route exact path="/register/:key/:id/:emailEnc?">
              <RegisterPassword />
            </Route>
            <Route exact path="/signin">
              <SignIn />
            </Route>
            <Route exact path="/live">
              <Live />
            </Route>
            <Route exact path="/signup">
              <SignUp />
            </Route>
            <Route exact path="/addH/:id">
              <AddOrganization />
            </Route>
            {/* Removed hospitality / sales / CRM tabs → ActivityLogs. */}
            <Route exact path="/rooms" render={toActivityLogs} />
            <Route exact path="/rooms/actions/:id" render={toActivityLogs} />
            <Route exact path="/hospitality" render={toActivityLogs} />
            <Route exact path="/hospitality/actions/:id" render={toActivityLogs} />
            <Route exact path="/hospitality/sales" render={toActivityLogs} />
            <Route exact path="/hospitality/refunds" render={toActivityLogs} />
            <Route
              exact
              path="/hospitality/departments"
              render={toDepartments}
            />
            <Route
              exact
              path="/hospitality/departments/:subtab"
              render={({ match, location }) => (
                <Redirect
                  to={{
                    pathname: ERP_PATHS.departmentsSub(match.params.subtab),
                    search: location.search,
                  }}
                />
              )}
            />
            <Route exact path="/hospitality/inventory" render={toActivityLogs} />
            <Route
              exact
              path="/hospitality/audits"
              render={({ location }) => (
                <Redirect to={{ pathname: ERP_PATHS.activityLogs, search: location.search }} />
              )}
            />
            <Route
              exact
              path="/hospitality/audits/:action"
              render={({ match, location }) => (
                <Redirect
                  to={{
                    pathname: ERP_PATHS.activityLogsAction(match.params.action),
                    search: location.search,
                  }}
                />
              )}
            />
            <Route
              exact
              path="/hospitality/metrics"
              render={({ location }) => (
                <Redirect to={{ pathname: ERP_PATHS.metrics, search: location.search }} />
              )}
            />
            <Route exact path="/hospitality/crm/:tab/:messageId" render={toActivityLogs} />
            <Route exact path="/hospitality/crm/:tab" render={toActivityLogs} />
            <Route exact path="/hospitality/crm" render={toActivityLogs} />
            <Route exact path="/hospitality/settings/editroom/:id" render={toActivityLogs} />
            <Route exact path="/hospitality/settings/add-rooms" render={toActivityLogs} />
            <Route exact path="/hospitality/settings/room-types" render={toActivityLogs} />
            <Route exact path="/hospitality/settings/rate-plans" render={toActivityLogs} />
            <Route exact path="/sales-distribution" render={toDepartments} />
            <Route
              path={`/${ERP_REPORTING_SLUG_LEGACY}`}
              render={({ location }) => {
                if (location.pathname === `/${ERP_REPORTING_SLUG_LEGACY}`) {
                  return (
                    <Redirect
                      to={{
                        pathname: (token && canAccessMetrics(permission))
                          ? ERP_PATHS.commandCenter
                          : ERP_PATHS.records,
                        search: location.search,
                      }}
                    />
                  );
                }
                return (
                  <Redirect
                    to={{
                      pathname: location.pathname.replace(
                        `/${ERP_REPORTING_SLUG_LEGACY}`,
                        ERP_REPORTING_BASE,
                      ),
                      search: location.search,
                      state: location.state,
                    }}
                  />
                );
              }}
            />
            {/* Legacy asset-renting / desk URLs → Activity logs. */}
            <Route exact path="/hospitality/operations" render={toActivityLogs} />
            <Route exact path="/hospitality/operations/actions/:id" render={toActivityLogs} />
            <Route exact path="/hospitality/reservations" render={toActivityLogs} />
            <Route exact path="/hospitality/rooms" render={toActivityLogs} />
            <Route exact path="/hospitality/board" render={toActivityLogs} />
            {/* Former /hospitality module prefix → /operations (settings + bookmarks). */}
            <Route
              path={`/${ERP_OPERATIONS_SLUG_LEGACY}`}
              render={({ location }) => (
                <Redirect
                  to={{
                    pathname: location.pathname.replace(
                      `/${ERP_OPERATIONS_SLUG_LEGACY}`,
                      `/${ERP_OPERATIONS_SLUG}`,
                    ) || `/${ERP_OPERATIONS_SLUG}`,
                    search: location.search,
                    state: location.state,
                  }}
                />
              )}
            />
            <Route
              exact
              path="/logs"
              render={({ location }) => (
                <Redirect to={{ pathname: ERP_PATHS.activityLogs, search: location.search }} />
              )}
            />
            <Route
              exact
              path="/logs/:action"
              render={({ match, location }) => (
                <Redirect
                  to={{
                    pathname: ERP_PATHS.activityLogsAction(match.params.action),
                    search: location.search,
                  }}
                />
              )}
            />
            <Route exact path="/branches" render={toDepartments} />
            <Route
              exact
              path="/branches/:subtab"
              render={({ match, location }) => (
                <Redirect
                  to={{
                    pathname: ERP_PATHS.departmentsSub(match.params.subtab),
                    search: location.search,
                  }}
                />
              )}
            />
            <Route exact path="/dashboard" render={toActivityLogs} />
            <Route exact path="/sales" render={toActivityLogs} />
            <Route exact path="/refunds" render={toActivityLogs} />
            <Route exact path="/reservations" render={toActivityLogs} />
            <Route exact path="/walk-ins" render={toActivityLogs} />
            <Route exact path="/departments" render={toDepartments} />
            <Route
              exact
              path="/departments/:subtab"
              render={({ match, location }) => (
                <Redirect
                  to={{
                    pathname: ERP_PATHS.departmentsSub(match.params.subtab),
                    search: location.search,
                  }}
                />
              )}
            />
            <Route exact path="/inventory" render={toActivityLogs} />
            <Route
              exact
              path="/audits"
              render={({ location }) => (
                <Redirect to={{ pathname: ERP_PATHS.activityLogs, search: location.search }} />
              )}
            />
            <Route
              exact
              path="/audits/:action"
              render={({ match, location }) => (
                <Redirect
                  to={{
                    pathname: ERP_PATHS.activityLogsAction(match.params.action),
                    search: location.search,
                  }}
                />
              )}
            />
            <Route
              exact
              path="/metrics"
              render={({ location }) => (
                <Redirect to={{ pathname: ERP_PATHS.metrics, search: location.search }} />
              )}
            />
            <Route
              exact
              path="/settings/editroom/:id"
              render={toActivityLogs}
            />
            <Route
              exact
              path="/settings/:subtab/:plan"
              render={({ match, location }) => (
                <Redirect
                  to={{
                    pathname: ERP_PATHS.settingsPlan(match.params.subtab, match.params.plan),
                    search: location.search,
                  }}
                />
              )}
            />
            <Route
              exact
              path="/settings/:subtab"
              render={({ match, location }) => {
                const { subtab } = match.params;
                const roomSetupTabs = ['add-rooms', 'room-types', 'rate-plans'];
                if (roomSetupTabs.includes(subtab)) {
                  return <Redirect to={{ pathname: ERP_PATHS.activityLogs, search: location.search }} />;
                }
                return (
                  <Redirect
                    to={{ pathname: ERP_PATHS.settingsSub(subtab), search: location.search }}
                  />
                );
              }}
            />
            <Route
              exact
              path="/settings"
              render={({ location }) => (
                <Redirect to={{ pathname: ERP_PATHS.settings, search: location.search }} />
              )}
            />
            <Route exact path="/reserve" render={toActivityLogs} />
            <Route
              exact
              path="/profile"
              render={() => <Redirect to={ERP_PATHS.settingsAccount} />}
            />
            {
              token && canAccessMasterDataNav(permission) && (
                <Route
                  exact
                  path={[
                    ERP_PATHS.records,
                    `${ERP_PATHS.records}/:subtab`,
                    `${ERP_PATHS.records}/:subtab/:id`,
                    `${ERP_PATHS.records}/:subtab/:id/:action`,
                  ]}
                >
                  <Records />
                </Route>
              )
            }
            {
              token && canAccessActivityLogsNav(permission) && (
                <Route exact path={ERP_PATHS.activityLogs}>
                  <Logs />
                </Route>
              )
            }
            {
              token && (
                <Route exact path={erpSettingsRoutePaths()}>
                  <Settings />
                </Route>
              )
            }
            {
              /* Associate Manager+ can open timed /logs/:action (e.g. Compliance review). */
              token && (permission || 0) >= PERM.SUB_MANAGER && (
                <Route exact path={`${ERP_PATHS.activityLogs}/:action`}>
                  <Logs />
                </Route>
              )
            }
            {
              token && canAccessMetricsNav(permission) && (
                <Route exact path={ERP_PATHS.metrics}>
                  <Metrics />
                </Route>
              )
            }
            {
              token && canAccessCommandCenterNav(permission) && (
                <Route exact path={ERP_PATHS.commandCenter}>
                  <CommandCenter />
                </Route>
              )
            }
            {
              token && canAccessFormsNav(permission) && (
                <Route
                  exact
                  path={[
                    ERP_PATHS.forms,
                    `${ERP_PATHS.forms}/workflow/:token`,
                    `${ERP_PATHS.forms}/report/:itemId`,
                    `${ERP_PATHS.forms}/report-request/:itemId`,
                    // Legacy bookmark — old /forms/:token workflow links.
                    `${ERP_PATHS.forms}/:token`,
                  ]}
                >
                  <Forms />
                </Route>
              )
            }
            {
              token && (
                <Route
                  exact
                  path={[
                    ERP_PATHS.reporting,
                    `${ERP_PATHS.reporting}/:subtab`,
                  ]}
                >
                  <Reporting />
                </Route>
              )
            }
            {
              token && (
                <Route
                  exact
                  path={[
                    ERP_PATHS.compliance,
                    `${ERP_PATHS.compliance}/:subtab`,
                  ]}
                >
                  <Compliance />
                </Route>
              )
            }
            {
              token && canAccessCompanyDepartmentsPage(permission) && (
                <Route
                  exact
                  path={[
                    ERP_PATHS.orgDepartments,
                    `${ERP_PATHS.orgDepartments}/:subtab`,
                  ]}
                  render={toOrgDepartmentsRecords}
                />
              )
            }
            {
              token && canAccessCompanyDepartmentsPage(permission) && (
                <Route
                  exact
                  path={[
                    ERP_PATHS.workflow,
                    `${ERP_PATHS.workflow}/:subtab`,
                  ]}
                  render={toWorkflowRecords}
                />
              )
            }
            {/* Leftover CRM / sales URLs after those tabs were removed. */}
            <Route path={['/crm', '/sales-distribution']} render={toActivityLogs} />
            <Route exact path="*">
              <NotFound />
            </Route>
          </Switch>
        </Suspense>
      </Layout>
    </React18BrowserRouter>
  );
};

export default Routing;
